import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { request } from "@/lib/coachQueries";

/**
 * Master v5 Prompt 17 — Journey Configuration. Coach-only. Every
 * mutation here is a pure preview/publish call against
 * journey_config_repository.py — never a locally-faked rail render.
 */
export function useJourneyDraft(workspaceId) {
  return useQuery({
    queryKey: ["journey-draft", workspaceId],
    enabled: !!workspaceId,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/journey/draft`),
  });
}

export function useJourneyPreview(workspaceId) {
  return useMutation({
    mutationFn: ({ stageSpecs, targetMembershipId }) =>
      request(`/api/rehearsal-room/workspaces/${workspaceId}/journey/preview`, {
        method: "POST",
        body: JSON.stringify({ stage_specs: stageSpecs, target_membership_id: targetMembershipId }),
      }),
  });
}

export function useJourneyPublish(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (stageSpecs) =>
      request(`/api/rehearsal-room/workspaces/${workspaceId}/journey/publish`, {
        method: "POST",
        body: JSON.stringify({ stage_specs: stageSpecs }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["journey-draft", workspaceId] });
      qc.invalidateQueries({ queryKey: ["journey-history", workspaceId] });
    },
  });
}

export function useJourneyVersionDetail(workspaceId, versionNumber, targetMembershipId) {
  return useQuery({
    queryKey: ["journey-version-detail", workspaceId, versionNumber, targetMembershipId],
    enabled: !!workspaceId && !!versionNumber && !!targetMembershipId,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/journey/history/${versionNumber}?target_membership_id=${targetMembershipId}`),
  });
}

export function useJourneyHistory(workspaceId) {
  return useQuery({
    queryKey: ["journey-history", workspaceId],
    enabled: !!workspaceId,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/journey/history`),
  });
}

// Master v5 Prompt 18 — Complete Session's "show the client-facing Home
// journey preview before publishing stage changes." Never persists.
export function useJourneyStatePreview(workspaceId) {
  return useMutation({
    mutationFn: ({ targetMembershipId, stateOverrides }) =>
      request(`/api/rehearsal-room/workspaces/${workspaceId}/journey/state-preview`, {
        method: "POST",
        body: JSON.stringify({ target_membership_id: targetMembershipId, state_overrides: stateOverrides }),
      }),
  });
}
